import React, { useId, useMemo } from 'react';

import { Label } from 'cpool-components/general/Label';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useApplicantInfoContext } from '~/features/leadDetails/context/ApplicantInfoContext.tsx';

type Props = {
    title: string;
    name: string;
    translationPath: string;
    values: string[];
    disabled?: boolean;
};

const TitleCombobox: React.FC<Props> = ({ disabled, name, title, translationPath, values }) => {
    const { control } = useFormContext();
    const { applicantId } = useApplicantInfoContext();
    const { t } = useTranslation();
    const labelId = useId();

    const options = useMemo(
        () =>
            values.map((value) => ({
                // @ts-expect-error ts doesn't recognize the ${translationPath}.${value} as a valid value
                label: t(`${translationPath}.${value}`) as string,
                value,
            })),
        [t, translationPath, values],
    );

    return (
        <FormField<FieldValues, string | null> control={control} name={name}>
            {({ error, onChange, value }) => (
                <div className="flex items-start gap-2.5">
                    <Label className="h-8 w-[175px] shrink-0 justify-start" id={labelId}>
                        {title}
                    </Label>
                    <DropdownMenu
                        aria-labelledby={labelId}
                        data-testid={`${name}-${applicantId}`}
                        options={options}
                        value={value ?? undefined}
                        onChange={(newValue) => onChange(newValue as string)}
                        placeholder=""
                        triggerClassName="w-full justify-between"
                        className="w-full"
                        disabled={disabled}
                        error={error}
                        searchable
                        scrollable
                    />
                </div>
            )}
        </FormField>
    );
};

export default TitleCombobox;
